import { convertCentsToDollarsDecimal } from '@/utils/helpers';
import { ApiPaginationParams, ApiSortParams } from '@/types/api';
import type { AgreementAthleteStatusEnum, AgreementStatusEnum } from './agreements';
import type { AgreementAthlete } from './agreements';
import type { EligibleAthlete } from './athletes';
import type { AcademicYear } from './academicYears';
import type { ApiPaymentHistoryEventCompact } from './paymentHistory';

export const WALLET_STATUS_ENUM = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  NOT_FOUND: 'not_found',
  STATUS_UNAVAILABLE: 'status_unavailable',
} as const;

export const WALLET_VERIFICATION_STATUS_ENUM = {
  ACTIVE: 'active',
  PENDING_APPLICATION: 'pending_application',
  UNDER_REVIEW: 'under_review',
  DOCUMENTS_REQUIRED: 'documents_required',
  SUSPENDED: 'suspended',
  UNKNOWN: 'unknown',
} as const;

export const PAYMENT_REQUEST_STATUS_ENUM = {
  CREATED: 0,
  SENDING: 20,
  SENT: 40,
  SCHEDULED: 50,
  SENDING_FAILED: 60,
  PAID: 100,
  PAID_OUTSIDE_GM: 110,
  CANCELED: 200,
} as const;

export const PAYMENT_MOMENT_ENUM = {
  UPCOMING: 'upcoming',
  PAST: 'past',
  ALL: 'all',
} as const;

export const PAYMENT_REQUEST_TYPE_ENUM = {
  ROYALTIES: 'royalties',
  SERVICES: 'services',
  ALSTON: 'alston',
  ALSTON_ALLOWANCE: 'alston_allowance',
} as const;

export const ACADEMIC_YEAR_MONTHS = ['july', 'august', 'september', 'october', 'november', 'december', 'january', 'february', 'march', 'april', 'may', 'june'] as const;

export type AcademicYearMonth = typeof ACADEMIC_YEAR_MONTHS[number];

export const ALLOCATION_STATUS = {
  SENT: 'sent',
  PAID: 'paid',
  PAID_OUTSIDE_GM: 'paidOutsideGm',
  CONTRACTED: 'contracted',
} as const;

export type AllocationStatus = typeof ALLOCATION_STATUS[keyof typeof ALLOCATION_STATUS];

export type MonthlyAllocationEntry = {
  [ALLOCATION_STATUS.SENT]: number;
  [ALLOCATION_STATUS.PAID]: number;
  [ALLOCATION_STATUS.PAID_OUTSIDE_GM]: number;
  [ALLOCATION_STATUS.CONTRACTED]: number;
  /** Sum of sent + paid + paidOutsideGm */
  totalPaid: number;
  /** Sum of contracted + totalPaid */
  total: number;
};

export type MonthlyAllocation = Record<string, MonthlyAllocationEntry>;

export type PaymentRequestStatus = typeof PAYMENT_REQUEST_STATUS_ENUM[keyof typeof PAYMENT_REQUEST_STATUS_ENUM];

export type ParsedPaymentStatus = 'notPayable' | 'ready' | 'sendingPayment' | 'sent' | 'scheduled' | 'failedToSend' | 'canceled' | 'paid' | 'paidOutsideGM';

export type WalletStatus = typeof WALLET_STATUS_ENUM[keyof typeof WALLET_STATUS_ENUM];

export type WalletVerificationStatus = typeof WALLET_VERIFICATION_STATUS_ENUM[keyof typeof WALLET_VERIFICATION_STATUS_ENUM];

export type PaymentMoment = typeof PAYMENT_MOMENT_ENUM[keyof typeof PAYMENT_MOMENT_ENUM];

export type PaymentRequestType = typeof PAYMENT_REQUEST_TYPE_ENUM[keyof typeof PAYMENT_REQUEST_TYPE_ENUM];

export interface ApiPaymentRequest {
  twrn: string;
  athlete: {
    agreement_athlete_status: AgreementAthleteStatusEnum;
    agreement_athlete_twrn: string;
    twrn: string;
    wallet_status: WalletStatus;
    wallet_verification_status: WalletVerificationStatus | null;
    legal_name: string | null;
    business_name: string | null;
    preferred_payout_customer_name: string | null;
    is_international: boolean;
  };
  amount: number;
  date: string;
  status: PaymentRequestStatus;
  wallet_payment_type: PaymentRequestType | null;
}

export class PaymentRequest {
  twrn: string;
  athlete: {
    agreementAthleteStatus: AgreementAthleteStatusEnum;
    agreementAthleteTwrn: string;
    twrn: string;
    walletStatus: WalletStatus;
    walletVerificationStatus: WalletVerificationStatus | null;
    legalName: string | null;
    businessName: string | null;
    preferredPayoutCustomerName: string | null;
    isInternational: boolean;
  };

  amount: number;
  date: string;
  status: PaymentRequestStatus;
  walletPaymentType: PaymentRequestType | null;

  constructor(data: ApiPaymentRequest) {
    this.twrn = data.twrn;
    this.date = data.date;
    this.athlete = {
      agreementAthleteStatus: data.athlete.agreement_athlete_status,
      agreementAthleteTwrn: data.athlete.agreement_athlete_twrn,
      twrn: data.athlete.twrn,
      walletStatus: data.athlete.wallet_status,
      walletVerificationStatus: data.athlete.wallet_verification_status,
      legalName: data.athlete.legal_name,
      businessName: data.athlete.business_name,
      preferredPayoutCustomerName: data.athlete.preferred_payout_customer_name,
      isInternational: data.athlete.is_international,
    };
    this.amount = convertCentsToDollarsDecimal(data.amount);
    this.status = data.status;
    this.walletPaymentType = data.wallet_payment_type;
  }
}

export interface ApiPaymentRequestWithAgreement extends ApiPaymentRequest {
  agreement: {
    twrn: string;
    name: string;
    status: AgreementStatusEnum;
  };
}

export class PaymentRequestWithAgreement extends PaymentRequest {
  agreement: {
    twrn: string;
    name: string;
    status: AgreementStatusEnum;
  };

  constructor(data: ApiPaymentRequestWithAgreement) {
    super(data);
    this.agreement = {
      twrn: data.agreement.twrn,
      name: data.agreement.name,
      status: data.agreement.status,
    };
  }
}

// Payment Request Details
export interface ApiPaymentRequestDetails {
  twrn: string;
  athlete: {
    agreement_athlete_twrn: string;
    twrn: string;
    wallet_status: WalletStatus;
    wallet_verification_status: WalletVerificationStatus | null;
    legal_name: string | null;
    business_name: string | null;
    preferred_payout_customer_name: string | null;
  };
  amount: number;
  date: string;
  status: PaymentRequestStatus;
  wallet_payment_type: PaymentRequestType | null;
  /** Compact flat timeline events from v2 show-payment. */
  payment_status_changes: ApiPaymentHistoryEventCompact[];
}

export class PaymentRequestDetails {
  twrn: string;
  athlete: {
    agreementAthleteTwrn: string;
    twrn: string;
    walletStatus: WalletStatus;
    walletVerificationStatus: WalletVerificationStatus | null;
    legalName: string | null;
    businessName: string | null;
    preferredPayoutCustomerName: string | null;
  };

  amount: number;
  /** Raw API amount in cents (for history event hydration). */
  amountInCents: number;
  date: string;
  status: PaymentRequestStatus;
  walletPaymentType: PaymentRequestType | null;
  paymentStatusChanges: ApiPaymentHistoryEventCompact[];
  constructor(data: ApiPaymentRequestDetails) {
    this.twrn = data.twrn;
    this.date = data.date;
    this.athlete = {
      agreementAthleteTwrn: data.athlete.agreement_athlete_twrn,
      twrn: data.athlete.twrn,
      walletStatus: data.athlete.wallet_status,
      walletVerificationStatus: data.athlete.wallet_verification_status,
      legalName: data.athlete.legal_name,
      businessName: data.athlete.business_name,
      preferredPayoutCustomerName: data.athlete.preferred_payout_customer_name,
    };
    this.amountInCents = data.amount;
    this.amount = convertCentsToDollarsDecimal(data.amount);
    this.status = data.status;
    this.walletPaymentType = data.wallet_payment_type;
    this.paymentStatusChanges = data.payment_status_changes ?? [];
  }
}

export type ApiPaymentRequestListingRequestBody = ApiPaginationParams & {
  athletes?: string[];
  payment_statuses?: (PaymentRequestStatus | -1)[];
  wallet_statuses?: WalletStatus[];
  agreement_athlete_statuses?: AgreementAthleteStatusEnum[];
  dates?: {
    start: string;
    end: string;
  } | undefined;
  sort?: ApiSortParams;
  ready_only?: boolean;
};


// Update Payment Request
export type UpdatePaymentRequestRequestModel = {
  amount: number;
  date: string;
  reason: string;
  notify_athlete?: boolean;
};


// Used in the Payment Requests Table and related components
export type PaymentRequestWithAthlete = Omit<PaymentRequest, 'athlete'> & {
  athlete: (AgreementAthlete | EligibleAthlete) & {
    agreementAthleteStatus: AgreementAthleteStatusEnum;
    agreementAthleteTwrn: string;
    walletStatus: WalletStatus;
    walletVerificationStatus: WalletVerificationStatus | null;
    legalName: string | null;
    businessName: string | null;
    preferredPayoutCustomerName: string | null;
    isInternational: boolean;
  };
  agreementTwrn: string;
  agreement?: PaymentRequestWithAgreement['agreement'];
};

export type ApiMonthlyAllocationEntry = {
  sent: number;
  paid: number;
  paid_outside_gm: number;
  contracted: number;
};

export type ApiMonthlyAllocation = Record<string, ApiMonthlyAllocationEntry>;

export type ApiAnnualPayment = {
  twrn: string;
  full_name: string;
  profile_photo_url: string;
  jersey: number;
  position: string;
  academic_year: string;
  total_paid_volume: number;
  total_contracted: number;
  allocations: ApiMonthlyAllocation;
};


export class AnnualPayment {
  twrn: string;
  fullName: string;
  profilePhotoUrl: string;
  jerseyNumber: number;
  position: string;
  academicYear: string;
  totalPaidVolume: number;
  totalContracted: number;
  allocations: MonthlyAllocation;

  constructor(data: ApiAnnualPayment) {
    this.twrn = data.twrn;
    this.fullName = data.full_name;
    this.profilePhotoUrl = data.profile_photo_url;
    this.jerseyNumber = data.jersey;
    this.position = data.position;
    this.academicYear = data.academic_year;
    this.totalPaidVolume = convertCentsToDollarsDecimal(data.total_paid_volume);
    this.totalContracted = convertCentsToDollarsDecimal(data.total_contracted);
    this.allocations = Object.fromEntries(
      Object.entries(data.allocations).map(([month, entry]) => {
        const sent = convertCentsToDollarsDecimal(entry.sent);
        const paid = convertCentsToDollarsDecimal(entry.paid);
        const paidOutsideGm = convertCentsToDollarsDecimal(entry.paid_outside_gm);
        const contracted = convertCentsToDollarsDecimal(entry.contracted);
        const totalPaid = sent + paid + paidOutsideGm;
        return [month, {
          sent,
          paid,
          paidOutsideGm,
          contracted,
          totalPaid,
          total: contracted + totalPaid,
        }];
      }),
    );
  }
}

export type ApiAnnualPaymentsResponse = ApiAnnualPayment[];

export type ApiTeamPaymentsStats = {
  total_payments: number;
  total_payments_sent_to_wallet: number;
  total_payment_value_sent_to_wallet: number;

};

export class TeamPaymentsStats {
  totalPayments: number;
  totalPaymentsSentToWallet: number;
  totalPaymentValueSentToWallet: number;

  constructor(data: ApiTeamPaymentsStats) {
    this.totalPayments = data.total_payments;
    this.totalPaymentsSentToWallet = data.total_payments_sent_to_wallet;
    this.totalPaymentValueSentToWallet = convertCentsToDollarsDecimal(data.total_payment_value_sent_to_wallet);
  }
}

export type ApiTeamPaymentsStatsResponse = ApiTeamPaymentsStats;

export type PeriodFilterValue
  = | {
    mode: 'academic_year';
    academicYear: AcademicYear;
  }
  | {
    mode: 'months';
    months: string[];
  };
